import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocale } from '../context/LocaleContext';
import {
  APPAREL_TYPES,
  FILTER_SIZES,
  GENDERS,
  baseAmountToDisplay,
  countActiveFilters,
  displayAmountToBase,
  filtersToSearchParams,
  getLocalizedPricePresets,
  parseFilterParams,
} from '../utils/filters';
import './ProductFilters.css';

function FilterGroup({ title, children }) {
  return (
    <div className="product-filters-group">
      <p className="product-filters-label">{title}</p>
      <div className="product-filters-options">{children}</div>
    </div>
  );
}

function Chip({ active, children, onClick }) {
  return (
    <button
      type="button"
      className={`product-filters-chip${active ? ' active' : ''}`}
      aria-pressed={active}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function isPresetActive(preset, filters) {
  const min = filters.minPrice === '' ? null : Number(filters.minPrice);
  const max = filters.maxPrice === '' ? null : Number(filters.maxPrice);

  if (preset.max == null) {
    return min === preset.min && max == null;
  }

  if (preset.min === 0) {
    return (min == null || min === 0) && max === preset.max;
  }

  return min === preset.min && max === preset.max;
}

function ProductFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { currency, currencyConfig } = useLocale();
  const filters = useMemo(() => parseFilterParams(searchParams), [searchParams]);
  const activeCount = countActiveFilters(filters);
  const pricePresets = useMemo(() => getLocalizedPricePresets(currency), [currency]);
  const displayMin = baseAmountToDisplay(filters.minPrice, currency);
  const displayMax = baseAmountToDisplay(filters.maxPrice, currency);

  const updateFilters = next => {
    setSearchParams(filtersToSearchParams(searchParams, { ...filters, ...next }), {
      replace: true,
    });
  };

  const clearFilters = () => {
    updateFilters({
      apparel: '',
      gender: '',
      size: '',
      minPrice: '',
      maxPrice: '',
    });
  };

  const body = (
    <>
      <div className="product-filters-head">
        <p className="product-filters-title">Filters</p>
        {activeCount > 0 && (
          <button type="button" className="product-filters-clear" onClick={clearFilters}>
            Clear ({activeCount})
          </button>
        )}
      </div>

      <FilterGroup title="Category">
        {APPAREL_TYPES.map(type => (
          <Chip
            key={type.slug}
            active={filters.apparel === type.slug}
            onClick={() =>
              updateFilters({
                apparel: filters.apparel === type.slug ? '' : type.slug,
              })
            }
          >
            {type.label}
          </Chip>
        ))}
      </FilterGroup>

      <FilterGroup title="Gender">
        {GENDERS.map(item => (
          <Chip
            key={item.slug}
            active={filters.gender === item.slug}
            onClick={() =>
              updateFilters({
                gender: filters.gender === item.slug ? '' : item.slug,
              })
            }
          >
            {item.label}
          </Chip>
        ))}
      </FilterGroup>

      <FilterGroup title="Size">
        {FILTER_SIZES.map(size => (
          <Chip
            key={size}
            active={filters.size === size}
            onClick={() =>
              updateFilters({
                size: filters.size === size ? '' : size,
              })
            }
          >
            {size}
          </Chip>
        ))}
      </FilterGroup>

      <FilterGroup title={`Price (${currencyConfig?.symbol || currency})`}>
        {pricePresets.map(preset => {
          const active = isPresetActive(preset, filters);

          return (
            <Chip
              key={preset.slug}
              active={active}
              onClick={() => {
                if (active) {
                  updateFilters({ minPrice: '', maxPrice: '' });
                  return;
                }

                if (preset.slug === '0-50') {
                  updateFilters({ minPrice: '', maxPrice: '50' });
                } else if (preset.slug === '200+') {
                  updateFilters({ minPrice: '200', maxPrice: '' });
                } else {
                  updateFilters({
                    minPrice: String(preset.min),
                    maxPrice: String(preset.max),
                  });
                }
              }}
            >
              {preset.label}
            </Chip>
          );
        })}
      </FilterGroup>

      <div className="product-filters-price-inputs">
        <label className="product-filters-field">
          Min
          <input
            type="number"
            min="0"
            inputMode="decimal"
            placeholder="0"
            value={displayMin}
            onChange={event =>
              updateFilters({
                minPrice: displayAmountToBase(event.target.value, currency),
              })
            }
          />
        </label>
        <span className="product-filters-price-sep">–</span>
        <label className="product-filters-field">
          Max
          <input
            type="number"
            min="0"
            inputMode="decimal"
            placeholder="Any"
            value={displayMax}
            onChange={event =>
              updateFilters({
                maxPrice: displayAmountToBase(event.target.value, currency),
              })
            }
          />
        </label>
      </div>
    </>
  );

  return (
    <aside className="product-filters" aria-label="Product filters">
      <button
        type="button"
        className="product-filters-mobile-toggle"
        onClick={() => setMobileOpen(open => !open)}
        aria-expanded={mobileOpen}
      >
        Filters{activeCount > 0 ? ` (${activeCount})` : ''}
      </button>

      <div className={`product-filters-panel${mobileOpen ? ' open' : ''}`}>{body}</div>
    </aside>
  );
}

export default ProductFilters;
